import { useState } from "react";
import { useLocation } from "react-router-dom";
import ApiHttp from "byzantine/src/ApiHttp";
import { Button, TextInput } from "@narmi/design_system";
import {
  ContextForm,
  useFormData,
  useLoadingContext,
  catcat as cc,
} from "cerulean";
import { useLocalization } from "@fluent/react";

import HeaderWithSteps from "../HeaderWithSteps";
import { fetchSudoCode } from "./utils";
import Container from "../Container";
import BackButton from "./BackButton";
import ActionBar from "./ActionBar";
import { useSudoContext } from "./SudoContext";
import { SudoEventEmitter, SudoEvents } from "./events";

import styles from "./SubmitOTP.module.scss";
import buttonStyles from "./ActionBar.module.scss";

interface SubmitOTPProps {
  isInDialog?: boolean;
}

const SubmitOTP = ({ isInDialog }: SubmitOTPProps) => {
  const location = useLocation();
  const { selectedDeviceId, helpMessage: initialHelpMessage } =
    location.state || {};

  const [helpMessage, setHelpMessage] = useState(initialHelpMessage?.message);
  const { requestOtpRoute } = useSudoContext();
  const { setIsLoading } = useLoadingContext() as {
    setIsLoading: (state: boolean) => void;
  };
  const { formData, onChange } = useFormData();
  const { l10n } = useLocalization();

  const sendCode = (callback: (error?: object) => void) => {
    ApiHttp.fetch(
      "sudo",
      { method: "POST" },
      { one_time_password: formData.one_time_password }
    )
      .then(() => {
        // Emit the established event to signal sudo completion
        SudoEventEmitter.emit(SudoEvents.ESTABLISHED);

        // Note: The navigation will be handled by the establishSudo promise resolution
        // so we don't need to navigate here anymore
      })
      .catch(() => {
        callback({
          one_time_password: l10n.getString(
            "error-incorrect-verification-code",
            null,
            "Incorrect verification code entered, please try again."
          ),
        });
      });
  };

  const requestCodeAgain = async () => {
    setIsLoading(true);
    const response = await fetchSudoCode(selectedDeviceId);
    if (response?.message) {
      setHelpMessage(response.message);
    }
    setIsLoading(false);
  };

  const subheader = (
    /*
    For most flows that use HeaderWithSteps, spacing b/t header and subheader is 8px on l/m, 4px on s/xs.
    For sudo, it's always 4px, so we need to subtract 4px here
    Also, we only want 20px of margin b/t the resend code button and the content (vs. the typical 40px)
    */
    <div style={{ marginTop: "-4px", marginBottom: "-20px" }}>
      {helpMessage || "A verification code was just sent to your device."}
      <div
        className={cc("margin--top--xs", {
          "padding--bottom--l": isInDialog,
        })}
      >
        <Button
          kind="plain"
          testId="resend-otp-button"
          onClick={requestCodeAgain}
          label={l10n.getString("button-resend-code", null, "Resend code")}
          size="s"
        />
      </div>
    </div>
  );

  const content = (
    <ContextForm data={formData} onChange={onChange} formID="submit-otp">
      <ContextForm.Field required>
        <TextInput
          id="one_time_password"
          field="one_time_password"
          label={l10n.getString(
            "label-verification-code",
            null,
            "Verification code"
          )}
        />
      </ContextForm.Field>
      <div className={cc({ [styles.actionBarWrapper]: isInDialog })}>
        <ActionBar align={isInDialog ? "right" : "center"}>
          <BackButton to={requestOtpRoute} />
          <div className={buttonStyles.continueButtonWrapper}>
            <ContextForm.Action
              onSubmit={sendCode}
              dangerouslyDisableShowLoading
            >
              <Button
                testId="submit-otp-button"
                type="submit"
                label={l10n.getString("button-continue", null, "Continue")}
              />
            </ContextForm.Action>
          </div>
        </ActionBar>
      </div>
    </ContextForm>
  );

  return isInDialog ? (
    <>
      <div className={styles.subheaderText}>{subheader}</div>
      {content}
    </>
  ) : (
    <Container>
      <HeaderWithSteps
        headerText={l10n.getString(
          "heading-enter-verification-code",
          null,
          "Enter verification code"
        )}
        subheaderText={subheader}
      />
      {content}
    </Container>
  );
};

export default SubmitOTP;
